import React, { useCallback } from 'react';
import styled, { css } from 'styled-components/macro';
import isEqual from 'lodash.isequal';

import useMedia from 'hooks/useMedia';
import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';
import { ABBREVIATION_THRESHOLD } from 'helpers/abbreviateNumber';
import formatNumber from 'helpers/formatNumber';
import formatDate from 'helpers/formatDate';
import { definitionLabel, definitionValue } from 'styles/base-classes';
import modularScale from 'styles/helpers/modular-scale';
import { ReactComponent as ToggledOffIcon } from 'icons/icon-hidden.svg';
import KeyValueLoader from '../utility/ContentLoader/KeyValueLoader/KeyValueLoader';
import Checkbox, { CheckboxContainer } from '../base/Checkbox/Checkbox';
import Box from 'components/elements/Box';
import { ThemeProps } from 'types/styled';
import { IDataTogglePairs } from '../data-containers/PerformanceTrend/types';
import AccessDataValue, { AccessDataValueWrapper } from './AccessDataValue';
import Span from 'components/elements/span';
import colors from 'styles/themes/colors';

export const DataList = styled('ul')<any>`
  list-style-type: none;
  min-height: 50px;
  margin: ${({ theme }): string =>
    `${theme.spacing.xsmall} 0 ${theme.spacing.large}`};

  ${({ newDesign, progVsOrg, isSmallScreen }): any =>
    newDesign && !isSmallScreen
      ? `width: ${progVsOrg ? '400px' : 'auto'}; span{ font-size: 13px;
    letter-spacing: 1.18px;}`
      : isSmallScreen &&
        css`
          span {
            font-size: 11px;
            letter-spacing: 1px;
            display: flex;
            align-items: center;
          }
          ${Value} {
            font-size: 16px;
          }
        `}

  @media screen and (min-width: 32.4375em) {
    display: flex;
    flex-flow: row wrap;
    margin: ${({ theme }): string =>
      `${theme.spacing.small} 0 ${theme.spacing.large}`};

    @supports (grid-gap: 1em) and
      (
        grid-template-columns:
          repeat(
            auto-fit,
            minmax(
              ${({ newDesign }): string => (newDesign ? '18' : '20')}ch,
              1fr
            )
          )
      ) {
      display: grid;
      grid-template-columns: repeat(
        auto-fit,
        minmax(${({ newDesign }): string => (newDesign ? '18' : '20')}ch, 1fr)
      );
      grid-gap: ${({ theme }): string => theme.spacing.medium};

      ${({ withAccessData, isSmallScreen }): any =>
        withAccessData &&
        isSmallScreen &&
        css`
        display: block;

        ${ToggleWrapper} {
          flex-flow: row wrap;

          ${AccessDataValueWrapper} {
              flex-basis: 100%;
            }
          }
        }
      `}
    }

    @media screen and (min-width: 1100px) {
      ${({ numberOfCols }): any =>
        numberOfCols &&
        css`
          @supports (grid-gap: 1em) and
            (grid-template-columns: repeat(${numberOfCols}, 1fr)) {
            display: grid;
            grid-template-columns: repeat(${numberOfCols}, 1fr);
            grid-gap: ${({ theme }): string => theme.spacing.medium};
          }
        `}
    }

    ${({ smallCols }): any =>
      smallCols &&
      css`
        @supports (grid-gap: 1em) and (grid-template-columns: repeat(6, 1fr)) {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          grid-gap: ${({ theme }): string => theme.spacing.medium};
        }
      `}

    ${({ withAccessData, isToggledOff }): any =>
      withAccessData &&
      css`
        ${DataListItem} {
          align-self: ${isToggledOff ? 'flex-start' : 'flex-end'};
        }
      `}
  }

  ${({ isStefDesign, newDesign, progVsOrg, withAccessData }): any =>
    !isStefDesign &&
    css`
      @media screen and (min-width: 50em) {
        margin: ${({ theme }): string =>
          `${theme.spacing.large} 0 ${theme.spacing.large} ${
            newDesign
              ? progVsOrg
                ? '108px'
                : '124px'
              : withAccessData
              ? '3.7em'
              : ''
          }`};
      }
    `};

  ${({ isStefDesign, isSmallScreen }): any =>
    isStefDesign &&
    isSmallScreen &&
    css`
      display: flex;
      justify-content: space-around;
      flex-wrap: wrap;

      @media screen and (max-width: 32.4375em) {
        padding: 0 12%;
      }

      ${Value} {
        font-size: 25px !important;
      }
    `};

  ${({ withAccessData, isSmallScreen }): any =>
    withAccessData &&
    isSmallScreen &&
    css`
      ${ToggleWrapper} {
        flex-flow: row wrap;

        ${AccessDataValueWrapper} {
          flex-basis: 100%;
        }

        ${Value} {
          font-size: 16px !important;
        }
        ${LabelText} {
          font-weight: 600;
        }
        ${IconWrapper} {
          font-size: 20px !important;
        }
        ${CheckboxContainer} {
          position: relative;
          top: 4px;
        }
      }
    `}
`;

export const DataListItem = styled('li')<any>`
  margin: ${({ theme }): string => `${theme.spacing.small} 0`};
  flex-basis: 50%;

  @media screen and (min-width: 32.4375em) {
    flex: 0 1 14em;
    margin: ${({ theme }): string => theme.spacing.medium};

    @supports (grid-gap: 1em) and
      (grid-template-columns: repeat(auto-fit, minmax(20ch, 1fr))) {
      grid-column: span 1;
      margin: 0;
      align-self: end;
    }
  }
`;

const DataToggle = styled('button')<any>`
  display: block;
  appearance: none;
  width: 100%;
  text-align: left;
  padding: 0;

  ${({ isColumnFlexed }): any =>
    isColumnFlexed &&
    css`
      ${ToggleWrapper} {
        flex-flow: column wrap;
        align-items: flex-start;
      }
    `}
`;

const ContentWrapper = styled('div')`
  display: flex;
  justify-content: space-between;

  span {
    width: max-content;
  }

  @media screen and (min-width: 32.4375em) {
    flex-flow: column nowrap;
    justify-content: flex-start;
  }
`;

const ToggleWrapper = styled('div')<any>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  min-height: ${({ isSmallScreen }): string =>
    isSmallScreen ? '28px' : '56px'};

  @media screen and (min-width: 32.4375em) {
    flex-flow: column nowrap;
    align-items: flex-start;
  }
`;

const Label = styled('span')`
  ${definitionLabel};
  display: inline-flex;
  flex-flow: row nowrap;
  align-items: flex-end;
  padding-right: ${({ theme }): string => theme.spacing.medium};
  margin-bottom: 0.25em;
  font-weight: 600;
`;

const LabelText = styled('span')`
  margin-top: 1px;
  font-weight: 600;
`;

const TrackName = styled('span')`
  font-family: 'Proxima Nova';
  font-size: 13px;
  font-weight: bold;
  color: ${({ theme }): string => theme.NightBlue};
`;

const ValuesWrapper = styled.div<any>`
  ${({ newDesign }): string =>
    newDesign
      ? `span {
  font-size: 24px;
  font-weight: 600;}`
      : ''}
  display: flex;
  align-items: baseline;
`;

const Value = styled.span<ThemeProps<'span'>>`
  ${definitionValue};
  font-size: ${({ isSmallScreen, newDesign }): string =>
    isSmallScreen
      ? '20px !important'
      : newDesign
      ? '24px !important'
      : modularScale(1)};
  color: ${({ dataColor, isToggledOff, theme }): string =>
    isToggledOff ? theme.CasperGrey : dataColor};
  white-space: nowrap;
  height: 1.266em;

  @media screen and (min-width: 32.4375em) {
    font-size: ${modularScale(3)};
  }
`;

const SecondaryValue = styled(Value)`
  font-size: ${modularScale(-1)};
  margin-left: ${(p): string => p.theme.spacing.small};

  @media screen and (min-width: 32.4375em) {
    font-size: ${modularScale(1)};
  }
`;

const IconWrapper = styled(Value)<any>`
  color: ${({ theme }): string => theme.CasperGrey};
  ${({ newDesign, isSmallScreen, isStefDesign }): string =>
    newDesign && !isSmallScreen && !isStefDesign ? 'height: 38px;' : ''}
  display: flex;
  align-items: center;

  svg {
    height: ${({ newDesign, isSmallScreen, isStefDesign }): number =>
      newDesign && !isSmallScreen && !isStefDesign ? 1.85 : 0.84}em;
    width: ${({ newDesign, isSmallScreen, isStefDesign }): number =>
      newDesign && !isSmallScreen && !isStefDesign ? 1.85 : 0.84}em;
  }
`;

export const FlexBox = styled(Box)<any>`
  width: 100%;
  justify-content: space-between;
  flex-flow: row wrap;
`;

interface IDataListLoaderProps {
  expectedItemCount: number;
  newDesign?: boolean;
  progVsOrg?: boolean;
}

/**
 * Return a KeyValue skeleton loader if there is no data yet. */
const DataListLoader: React.FC<IDataListLoaderProps> = (props) => {
  const { expectedItemCount, newDesign, progVsOrg } = props;
  const isMobile = useMedia('(max-width: 32.4375em)');

  return (
    <DataList newDesign={newDesign} progVsOrg={progVsOrg}>
      {Array(expectedItemCount)
        .fill(null)
        .map((_, index) => (
          <DataListItem key={`datalist-loader-item-${index}`}>
            <KeyValueLoader isMobile={isMobile} />
          </DataListItem>
        ))}
    </DataList>
  );
};

interface ITracksDetailsProps {
  track?: {
    name: string;
    releaseDate: Date;
    id: string;
  };
}

const TracksDetails: React.FC<ITracksDetailsProps> = (props) => (
  <Box display="flex" flexDirection="column">
    <TrackName>{props.track?.name}</TrackName>
    <Label>
      <LabelText>
        {formatDate(props.track?.releaseDate as Date, 'day-full')}
      </LabelText>
    </Label>
  </Box>
);

const tooltipData = [
  {
    label: 'YouTube UGC',
    tooltipContent: `UGC stands for User Generated Content.
    YouTube UGC in Opus refers to unofficial videos using our
    catalog in the background + official audio streams from the
    YouTube Music application.`,
  },
  {
    label: 'ugc views',
    tooltipContent: `UGC stands for User Generated Content.
    UGC is when a user uploads or uses an "Original Sound" that is matched to
    a WMG delivered track. (original content in which our content was identified)`,
  },
  {
    label: 'pgc views',
    tooltipContent: `PGC stands for Professionally/Partner Generated Content.
    PGC is when a user selects a sound in the Short Form Video app that is from our
    repertoire (label delivered content).`,
  },
  {
    label: 'ugc creations',
    tooltipContent: `UGC stands for User Generated Content.
    UGC is when a user uploads or uses an "Original Sound" thatis matched to
    a WMG delivered track. (original content in which our content was identified)`,
  },
  {
    label: 'pgc creations',
    tooltipContent: `PGC stands for Professionally/Partner Generated Content.
    PGC is when a user selects a sound in the Short Form Video app that is from our
    repertoire (label delivered content).`,
  },
  {
    label: 'views',
    tooltipContent: `The number of views of creations that use the song.`,
  },
  {
    label: 'creations',
    tooltipContent: `The number of creations (videos) that use the song.`,
  },
];

interface IDataToggleListProps {
  data: IDataTogglePairs[] | undefined;
  /** Handles clicking a toggle */
  handleClick?: (event: any) => void;
  /** The currently toggled items */
  toggledItems?: string[] | number[] | any[];
  /** Expected number of items that will be shown for the first time.
   * This is needed for the skeleton placeholder.
   */
  expectedItemCount: number;
  /** The loading state of its parent */
  loading: boolean;
  newDesign?: boolean;
  isStefDesign?: boolean;
  progVsOrg?: boolean;
  isSmallScreen?: boolean;
  withTooltip?: boolean;
  withAccessData?: boolean;
  trackCompareMode?: boolean;
  tracksInfo?: {
    name: string;
    releaseDate: Date;
    id: string;
  }[];
  disableUncheckAll?: boolean;
  currentMetric?: string;
  numberOfCols?: number;
  isFullValueDisplayed?: boolean;
}

/**
 * DataToggleList renders a list of label/value pairs, in buttons when necessary.
 * It is an interactive alternative for ComponentStatistics.
 *
 * @class DataToggleList
 * @extends {Component}
 */
const DataToggleList: React.FC<IDataToggleListProps> = (props) => {
  const {
    data,
    handleClick,
    toggledItems = [],
    loading,
    newDesign,
    progVsOrg,
    isSmallScreen,
    withTooltip,
    expectedItemCount,
    withAccessData,
    trackCompareMode,
    tracksInfo,
    isStefDesign,
    disableUncheckAll,
    currentMetric,
    numberOfCols,
    isFullValueDisplayed,
  } = props;

  const getToggleState = useCallback(
    (selector) => {
      if (toggledItems.includes(selector)) return true;

      return toggledItems.some((item) => {
        return typeof item === 'object' && isEqual(item, selector);
      });
    },
    [toggledItems]
  );

  if (!data) {
    // return datalist to keep the layout the same
    return <DataList />;
  }

  if (loading) {
    return (
      <DataListLoader
        expectedItemCount={expectedItemCount}
        newDesign={newDesign}
        progVsOrg={progVsOrg}
      />
    );
  }

  const oneIsToggledOff = data.find((item: any) =>
    getToggleState(item.selector)
  );

  const lastCheckedItem =
    disableUncheckAll && toggledItems.length === data.length - 1;

  return (
    <DataList
      data-pw="data-toggle-list"
      newDesign={newDesign}
      progVsOrg={progVsOrg}
      isSmallScreen={isSmallScreen}
      smallCols={withTooltip && !withAccessData}
      withAccessData={withAccessData}
      isToggledOff={!!oneIsToggledOff}
      isStefDesign={isStefDesign}
      numberOfCols={numberOfCols}
    >
      {data.map((item) => {
        const {
          key,
          label,
          value,
          secondaryValue,
          dataColor,
          selector,
          rawValue,
          accessData,
          percentage,
        } = item;
        const isToggledOff = getToggleState(selector);

        if (!label) return null;
        const fullValue = rawValue && formatNumber(rawValue, 'full', false);

        const onClick = (): void => handleClick && handleClick(selector);

        const track = tracksInfo?.find(
          (track: any) =>
            track.id === String(selector).split('-').slice(0, 2).join('-')
        );

        const dataWithTooltip = tooltipData.find(
          (data: any) => label.search(data.label) !== -1
        );

        return (
          <DataListItem key={key}>
            {selector && handleClick ? (
              <DataToggle
                isColumnFlexed={
                  (trackCompareMode || isToggledOff) && isSmallScreen
                }
              >
                <ToggleWrapper isSmallScreen={isSmallScreen}>
                  {trackCompareMode && <TracksDetails track={track} />}
                  <FlexBox
                    display={isSmallScreen && !isStefDesign ? 'flex' : 'block'}
                  >
                    <Box display="flex">
                      <Label id={key} onClick={onClick}>
                        {(!newDesign || (isSmallScreen && !isStefDesign)) && (
                          <Checkbox
                            color={dataColor}
                            enabledText="Showing chartline for"
                            disabledText="Hiding chartline for"
                            isChecked={!isToggledOff}
                            newLayout={!newDesign || isSmallScreen}
                          />
                        )}
                        <LabelText>
                          {dataWithTooltip ? (
                            <>
                              <span
                                style={{
                                  marginRight: isSmallScreen ? '4px' : '0',
                                }}
                              >
                                {label.replace(dataWithTooltip.label, '')}
                              </span>
                              <TippyToolTip
                                tooltipContent={dataWithTooltip.tooltipContent}
                                placement="bottom"
                                maxWidth="460px"
                                withDashedLine
                                enabled
                              >
                                {dataWithTooltip.label}
                              </TippyToolTip>
                            </>
                          ) : (
                            label
                          )}
                        </LabelText>
                        {currentMetric && (
                          <TippyToolTip
                            enabled={
                              !!rawValue &&
                              Math.abs(rawValue) >= ABBREVIATION_THRESHOLD
                            }
                            tooltipContent={fullValue}
                            canBeCopied
                          >
                            <Span
                              color={colors.NightBlue}
                              ml={1}
                              fontWeight={600}
                            >
                              {currentMetric === 'percents'
                                ? value
                                : percentage}
                            </Span>
                          </TippyToolTip>
                        )}
                      </Label>
                    </Box>

                    {isToggledOff ? (
                      <IconWrapper
                        onClick={onClick}
                        newDesign={newDesign}
                        isStefDesign={isStefDesign}
                        isSmallScreen={isSmallScreen}
                      >
                        {isStefDesign && (
                          <Checkbox
                            color={dataColor}
                            enabledText="Showing chartline for"
                            disabledText="Hiding chartline for"
                            isChecked={!isToggledOff}
                            onClick={onClick}
                          />
                        )}
                        <ToggledOffIcon />
                      </IconWrapper>
                    ) : (
                      <>
                        <ValuesWrapper newDesign={newDesign}>
                          {newDesign && (!isSmallScreen || isStefDesign) && (
                            <Checkbox
                              color={dataColor}
                              enabledText="Showing chartline for"
                              disabledText="Hiding chartline for"
                              isChecked={!isToggledOff}
                              onClick={onClick}
                              disabled={lastCheckedItem}
                            />
                          )}
                          <TippyToolTip
                            enabled={
                              !!rawValue &&
                              Math.abs(rawValue) >= ABBREVIATION_THRESHOLD
                            }
                            tooltipContent={fullValue}
                            canBeCopied
                          >
                            <Value
                              aria-labelledby={key}
                              isToggledOff={isToggledOff}
                              dataColor={newDesign ? '#001247' : dataColor}
                              data-pw={`trend-${key}`}
                              isSmallScreen={isSmallScreen}
                              onClick={!lastCheckedItem ? onClick : undefined}
                            >
                              {isSmallScreen && rawValue && withAccessData // we are not using row value in Source of Streams
                                ? formatNumber(rawValue, 'number')
                                : currentMetric === 'percents'
                                ? percentage
                                : isFullValueDisplayed
                                ? fullValue
                                : value}
                            </Value>
                          </TippyToolTip>
                          {secondaryValue && (
                            <SecondaryValue
                              aria-labelledby={key}
                              isToggledOff={isToggledOff}
                              dataColor={newDesign ? '#001247' : dataColor}
                              data-pw={`trend-secondary-${key}`}
                              onClick={!lastCheckedItem ? onClick : undefined}
                            >
                              ({secondaryValue})
                            </SecondaryValue>
                          )}
                        </ValuesWrapper>
                        {withAccessData && accessData && (
                          <AccessDataValue
                            onClick={onClick}
                            data={accessData}
                            isSmallScreen={isSmallScreen}
                          />
                        )}
                      </>
                    )}
                  </FlexBox>
                </ToggleWrapper>
              </DataToggle>
            ) : (
              <ContentWrapper>
                <Box display="flex" flexDirection="column">
                  {trackCompareMode && <TracksDetails track={track} />}
                </Box>
                <Box display="flex">
                  <Label id={key}>{label}</Label>
                </Box>
                {withTooltip ? (
                  <TippyToolTip
                    enabled={
                      !!rawValue &&
                      !isSmallScreen &&
                      Math.abs(rawValue) >= ABBREVIATION_THRESHOLD
                    }
                    tooltipContent={fullValue}
                    canBeCopied
                  >
                    <Value
                      aria-labelledby={key}
                      dataColor={dataColor}
                      data-pw={`trend-${key}`}
                      newDesign={newDesign && withAccessData}
                    >
                      {isSmallScreen && rawValue
                        ? formatNumber(rawValue, 'number')
                        : value}
                    </Value>
                  </TippyToolTip>
                ) : (
                  <Value
                    aria-labelledby={key}
                    dataColor={dataColor}
                    data-pw={`trend-${key}`}
                  >
                    {value}
                  </Value>
                )}
                {withAccessData && accessData && (
                  <AccessDataValue
                    data={accessData}
                    isSmallScreen={isSmallScreen}
                  />
                )}
              </ContentWrapper>
            )}
          </DataListItem>
        );
      })}
    </DataList>
  );
};

export default DataToggleList;
